import * as React from 'react';
import { useMemo } from 'react';

import { useQuery } from '@apollo/client';

import { useParams } from 'js/lib/useRouter';

import DegreeDescriptionPageUpsellsContext from 'bundles/degree-description/contexts/DegreeDescriptionPageUpsellsContext';
import type {
  GetUpsellCoursesByDegreeSlugQuery,
  GetUpsellCoursesByDegreeSlugQueryVariables,
} from 'bundles/degree-description/queries/__generated__/getUpsellCoursesForDegreeBySlug';
import type {
  GetUpsellsForDegreeBySlugQuery,
  GetUpsellsForDegreeBySlugQueryVariables,
} from 'bundles/degree-description/queries/__generated__/getUpsellsForDegreeBySlug';
import getUpsellCoursesForDegreeBySlug from 'bundles/degree-description/queries/getUpsellCoursesForDegreeBySlug.graphql';
import getUpsellsForDegreeBySlug from 'bundles/degree-description/queries/getUpsellsForDegreeBySlug.graphql';
import type { UpsellsByDegreeSlug } from 'bundles/degree-description/types';
import { getStackableItemsWithUpsellType } from 'bundles/degree-description/utils/utils';

export const DegreeDescriptionPageUpsellsProvider: React.LegacyFunctionComponentWithChildren = ({ children }) => {
  const { slug } = useParams();

  const { data: upsellsData } = useQuery<GetUpsellsForDegreeBySlugQuery, GetUpsellsForDegreeBySlugQueryVariables>(
    getUpsellsForDegreeBySlug,
    {
      skip: !slug,
      variables: {
        // @ts-expect-error TS2769 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        slug,
      },
      context: { clientName: 'gatewayGql' },
    }
  );

  const { data: upsellCoursesData } = useQuery<
    GetUpsellCoursesByDegreeSlugQuery,
    GetUpsellCoursesByDegreeSlugQueryVariables
  >(getUpsellCoursesForDegreeBySlug, {
    skip: !slug,
    variables: { degreeSlug: slug },
    ssr: false,
    context: { clientName: 'gatewayGql' },
  });

  const stackableItemsWithUpsellType: UpsellsByDegreeSlug = useMemo(() => {
    const listOfUpsellsLinkedtoThisDegree: UpsellsByDegreeSlug = upsellsData?.UpsellQueries?.getUpsellsBySlug || [];
    const standaloneUpsellCoursesByDegree = upsellCoursesData?.CoursesStackability?.queryUpsellCoursesByDegreeSlug;
    return getStackableItemsWithUpsellType(listOfUpsellsLinkedtoThisDegree, standaloneUpsellCoursesByDegree);
  }, [upsellsData, upsellCoursesData]);

  const queryData = {
    stackableItemsWithUpsellType,
  };

  return <DegreeDescriptionPageUpsellsContext value={queryData}>{children}</DegreeDescriptionPageUpsellsContext>;
};
